import React, { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { AstralBeamGlobalStyles } from 'astralbeam-design';
import { AppErrorBoundary } from 'components/common';
import {
  BridgeWalletProvider,
  ChainProvider,
  EthereumWalletProvider,
  QueryProvider,
  ServicesProvider,
  WalletContext,
} from 'contexts';
import { useCsprClick } from 'hooks';
import { Analytics } from 'components/common/Analytics';
import { ThemeWrapper } from 'theme';
import { initProxyWasmCache } from 'utils/wasm-cache';

/**
 * BeamApp is THE app shell. Every route renders inside it via <Outlet/>.
 *
 * It mounts the full provider stack (wallets, chains, services, query cache)
 * once, applies the AstralBeam global styles, and hides the CSPR.click
 * prebuilt chrome (the ClickProvider in main.tsx auto-injects its top bar;
 * we keep the provider alive — useClickRef depends on it — but the beam
 * renders its own chrome).
 */
const BeamApp = () => {
  const { activeAccount, isInitialized, signIn, buyCSPR, clickRef, isClickLoaded } = useCsprClick();

  useEffect(() => {
    document.body.classList.add('beam-standalone');

    return () => document.body.classList.remove('beam-standalone');
  }, []);

  // Pre-fetch and cache proxy WASM on app load for Safari compatibility
  useEffect(() => {
    initProxyWasmCache().catch(() => {
      // Silently fail - WASM fetched on demand if needed
    });
  }, []);

  return (
    <React.StrictMode>
      <QueryProvider>
        <ThemeWrapper>
          <AppErrorBoundary>
            <WalletContext.Provider value={{ activeAccount, isInitialized, signIn, buyCSPR, clickRef, isClickLoaded }}>
              <ChainProvider>
                <EthereumWalletProvider>
                  <BridgeWalletProvider>
                    <ServicesProvider>
                      <AstralBeamGlobalStyles />
                      <Suspense fallback={null}>
                        <Outlet />
                      </Suspense>
                      <Analytics />
                    </ServicesProvider>
                  </BridgeWalletProvider>
                </EthereumWalletProvider>
              </ChainProvider>
            </WalletContext.Provider>
          </AppErrorBoundary>
        </ThemeWrapper>
      </QueryProvider>
    </React.StrictMode>
  );
};

export default BeamApp;
